/*
 * File: CurrencyInput.tsx
 * Author: Austin Robinson
 * Date Created: 7/9/2022
 * Copyright (c) DrivingDoc 2022
 */
import { useEffect, useRef, useState } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import Backdrop from '@mui/material/Backdrop';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { QueryClientProvider, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useDialogs } from '@toolpad/core/useDialogs';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import minMax from 'dayjs/plugin/minMax';
import relativeTime from 'dayjs/plugin/relativeTime';
import { FormProvider, useForm } from 'react-hook-form';

import {
  getCoursePeriodOptions,
  getCoursePeriodQueryKey,
  listCoursePeriodsQueryKey,
  listCourseSectionsOptions,
  updateCoursePeriodMutation,
  getCourseInstructorPoolOptions,
} from '@drivingdoc/api-client/generated/react-query';
import { ListCoursePeriodsData } from '@drivingdoc/api-client/generated/types';
import { Options } from '@drivingdoc/api-client/generated/sdk';
import { CoursePeriod } from 'types/periods';
import { GetSectionsItem } from 'types/sections';
import { Instructor } from 'types/instructors';

import DateModified from 'components/Dialogs/components/DateModified';
import FeedbackDialog from 'components/Dialogs/FeedbackDialog';
import DateInput from 'components/Forms/DateInput';
import DateTimeInput from 'components/Forms/DateTimeInput';
import SelectInput from 'components/Forms/SelectInput';
import TextInput from 'components/Forms/TextInput';
import TimeInput from 'components/Forms/TimeInput';

import { getApiErrorMessage } from 'utils/getApiErrorMessage';

import { UpdateCoursePeriodFormSchema, UpdateCoursePeriodFormValues } from './FormSchema';

dayjs.extend(duration);
dayjs.extend(minMax);
dayjs.extend(relativeTime);

export default function useModifyCoursePeriodDialog(
  keysToInvalidate?: string[],
  //   onDoneUpdating?: (result: CoursePeriod) => void,
) {
  const [currentCoursePeriodId, setCurrentCoursePeriodId] = useState<string | null>(null);
  const dialogs = useDialogs();
  const queryClient = useQueryClient();
  const hasInitializedRef = useRef(false);

  // fetch course period
  const coursePeriodQuery = useQuery({
    ...getCoursePeriodOptions({
      path: {
        id: currentCoursePeriodId ?? '',
      },
    }),
    enabled: !!currentCoursePeriodId,
  });
  const coursePeriod = coursePeriodQuery.data as CoursePeriod | undefined;

  // fetch other course sections
  const { data: sections } = useQuery({
    ...listCourseSectionsOptions({
      query: {
        courseId: coursePeriod?.course_section?.course_id,
        page: 1,
        pageSize: 100,
      },
    }),
    enabled: !!coursePeriod?.course_section?.course_id,
  });
  const sectionRows = (sections as { data: GetSectionsItem[]; total: number } | undefined)?.data;

  // fetch course instructor pool
  const { data: instructors } = useQuery({
    ...getCourseInstructorPoolOptions({
      path: {
        id: coursePeriod?.course_section?.course_id ?? '',
      },
      query: {
        page: 1,
        pageSize: 100,
      },
    }),
    enabled: !!coursePeriod?.course_section?.course_id,
  });

  const instructorData = instructors as { data: Instructor[]; total: number } | undefined;

  // update course period mutation
  const updateCoursePeriod = useMutation(updateCoursePeriodMutation());

  const handleClose = () => {
    setCurrentCoursePeriodId(null);
  };

  const openModifyDriveDialog = (coursePeriodId: string) => {
    // initialScrollPosition.current = document.documentElement.scrollTop;
    setCurrentCoursePeriodId(coursePeriodId);
    // setOpen(true);
  };

  const form = useForm<UpdateCoursePeriodFormValues>({
    mode: 'all',
    resolver: zodResolver(UpdateCoursePeriodFormSchema),
    // values: {
    //   schedule_type: coursePeriod?.course_section.course.schedule_type!,
    //   section_id: coursePeriod?.course_section_id!,
    //   capacity: coursePeriod?.capacity ?? 1,
    //   date: dayjs(coursePeriod?.start).format('YYYY-MM-DD'),
    //   start: coursePeriod?.start!,
    //   end: coursePeriod?.end!,
    //   open_enrollment: coursePeriod?.open_enrollment!,
    //   close_enrollment: coursePeriod?.close_enrollment!,
    //   instructor: coursePeriod?.course_period_instructors?.[0].instructor_user_id!,
    // },
  });

  const { handleSubmit } = form;

  //   useEffect(() => {
  //     if (coursePeriod?.course_section?.course?.schedule_type === 'fixed') {
  //       form.setValue('capacity', coursePeriod?.course_section?.capacity);
  //     } else {
  //       form.setValue('capacity', coursePeriod?.capacity);
  //     }
  //   }, [coursePeriod, form]);

  useEffect(() => {
    if (coursePeriod && !hasInitializedRef.current) {
      const scheduleType = coursePeriod?.course_section.course.schedule_type;
      form.reset({
        schedule_type: scheduleType,
        section_id: coursePeriod?.course_section_id,
        capacity: scheduleType === 'fixed' ? coursePeriod?.course_section.capacity : (coursePeriod?.capacity ?? 1),
        date: dayjs(coursePeriod?.start).format('YYYY-MM-DD'),
        start: coursePeriod?.start,
        end: coursePeriod?.end,
        open_enrollment: coursePeriod?.open_enrollment,
        close_enrollment: coursePeriod?.close_enrollment,
        instructor: coursePeriod?.course_period_instructors?.[0].instructor_user_id,
      });

      // mark form as initialized
      hasInitializedRef.current = true;
    }
  }, [form.reset, coursePeriod]);

  // update the start and end date when date field changes
  useEffect(() => {
    // get the duration of the period
    const periodDuration = Math.abs(dayjs(coursePeriod?.end).diff(coursePeriod?.start, 'minutes'));

    // get the new start and end date and time
    const newDate = dayjs(form.getValues('date'));
    const newStartTime = dayjs(form.getValues('start'))
      .set('year', newDate.year())
      .set('month', newDate.month())
      .set('date', newDate.date());
    const newEndTime = newStartTime.add(periodDuration, 'minutes');

    // update the form values
    form.setValue('start', newStartTime.toISOString());
    form.setValue('end', newEndTime.toISOString());
  }, [form.watch('date')]);

  useEffect(() => {
    const defaultDuration = Math.abs(dayjs(coursePeriod?.end).diff(coursePeriod?.start, 'minutes'));
    const startDateTime = dayjs(form.watch('start'));
    form.setValue('end', startDateTime.add(defaultDuration, 'minutes').toISOString());

    // console.log(`New End Date: ${form.watch('end')}`);
  }, [form.watch('start')]);

  const onSubmit = async (values: UpdateCoursePeriodFormValues) => {
    try {
      await updateCoursePeriod.mutateAsync({
        path: {
          id: currentCoursePeriodId!,
        },
        body: {
          courseSectionId: values?.section_id,
          start: values?.start,
          end: values?.end,
          openEnrollment: values?.open_enrollment,
          closeEnrollment: values?.close_enrollment,
          capacity: values?.capacity,
          instructors: [values?.instructor],
        },
      });
      queryClient.invalidateQueries({
        queryKey: getCoursePeriodQueryKey({ path: { id: currentCoursePeriodId! } }),
      });
      queryClient.invalidateQueries({
        queryKey: listCoursePeriodsQueryKey({} as Options<ListCoursePeriodsData>),
        exact: false,
      });
      if (keysToInvalidate) {
        queryClient.invalidateQueries({ queryKey: keysToInvalidate });
      }
      handleClose();
      dialogs.open(FeedbackDialog, {
        title: 'Success',
        message: 'Successfully Updated Course Period',
        icon: 'success',
      });
    } catch (error) {
      dialogs.open(FeedbackDialog, {
        title: 'Error ',
        message: getApiErrorMessage(error),
      });
    }

    return values;
  };

  const ModifyDriveDialog = (
    <QueryClientProvider client={queryClient}>
      <Dialog fullWidth open={!!currentCoursePeriodId} onClose={handleClose}>
        <DialogTitle component="div" display="flex" justifyContent="space-between">
          Modify Course Period
        </DialogTitle>

        {!coursePeriod?.id && (
          <Box display="flex" justifyContent="center" alignItems="center">
            <CircularProgress />
          </Box>
        )}

        <FormProvider {...form}>
          <form onSubmit={handleSubmit(onSubmit)}>
            {!!coursePeriod?.id && !!sections && !!instructorData && (
              <DialogContent>
                <Grid container rowSpacing={3} columnSpacing={2}>
                  <Grid size={12}>
                    <Box display="flex" flexDirection="column" rowGap={0}>
                      <Box display="flex">
                        <Typography variant="body2" fontWeight="bold">
                          Course: &nbsp;
                        </Typography>
                        <Typography variant="body2" fontWeight="regular" color="text">
                          &nbsp;{coursePeriod?.course_section?.course?.title}
                        </Typography>
                      </Box>
                      <Box display="flex">
                        <Typography variant="body2" fontWeight="bold">
                          Section: &nbsp;
                        </Typography>
                        <Typography variant="body2" fontWeight="regular" color="text">
                          &nbsp;{coursePeriod?.course_section?.title}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>

                  <Grid size={6}>
                    <SelectInput
                      name="section_id"
                      label="Section"
                      options={sectionRows?.map((section) => ({ label: section?.title, value: section?.id }))}
                    />
                  </Grid>

                  <Grid size={6}>
                    <TextInput
                      name="capacity"
                      label="Registration Limit"
                      type="number"
                      InputProps={{ inputProps: { min: 1 } }}
                      disabled={coursePeriod?.course_section?.course?.schedule_type === 'fixed'}
                      helperText={
                        coursePeriod?.course_section?.course?.schedule_type === 'fixed'
                          ? 'Registration limit is fixed for fixed schedule courses'
                          : undefined
                      }
                    />
                  </Grid>

                  <Grid size={4}>
                    <DateInput name="date" label="Date" />
                  </Grid>

                  <Grid size={4}>
                    <TimeInput name="start" label="Start Time" />
                  </Grid>

                  <Grid size={4}>
                    <TimeInput name="end" label="End Time" />
                  </Grid>

                  {coursePeriod?.course_section?.course?.schedule_type === 'self_scheduled' && (
                    <>
                      <Grid size={6}>
                        <DateTimeInput
                          name="open_enrollment"
                          label="Open Enrollment"
                          maxDateTime={dayjs.min(dayjs(form.watch('start')), dayjs(form.watch('close_enrollment')))}
                          helperText={`${dayjs.duration(dayjs(form.watch('start')).diff(form.watch('open_enrollment'))).humanize()} before period starts`}
                        />
                      </Grid>

                      <Grid size={6}>
                        <DateTimeInput
                          name="close_enrollment"
                          label="Close Enrollment"
                          minDateTime={dayjs(form.watch('open_enrollment'))}
                          maxDateTime={dayjs(form.watch().start)}
                          helperText={`${dayjs.duration(dayjs(form.watch('start')).diff(form.watch('close_enrollment'))).humanize()} before period starts`}
                        />
                      </Grid>
                    </>
                  )}

                  <Grid size={12}>
                    {/* <MultiSelectInput
                                            name="instructors"
                                            label="Instructors"
                                            options={instructors?.map((instructor) => ({ 
                                                label: `${instructor?.user?.nickname ?? instructor?.user?.first_name ?? ''} ${instructor?.user?.des_id ?? ''}`, 
                                                value: instructor?.user_id
                                            })).sort((a, b) => (a.label > b.label) ? 1 : -1)}
                                        /> */}
                    <SelectInput
                      name="instructor"
                      label="Instructor"
                      options={instructorData?.data
                        ?.map((instructor) => ({
                          label: `${instructor?.user?.nickname ?? instructor?.user?.first_name ?? ''} ${instructor?.user?.last_name ?? ''}`,
                          value: instructor?.user_id,
                        }))
                        .sort((a, b) => (a.label > b.label ? 1 : -1))}
                    />
                  </Grid>

                  <Grid size={12}>
                    <Box display="flex" flexDirection="column" rowGap={1}>
                      <DateModified
                        prefix="Created: "
                        date={coursePeriod?.created_at}
                        user={coursePeriod?.created_by_user}
                      />
                      <DateModified
                        prefix="Updated: "
                        date={coursePeriod?.updated_at}
                        user={coursePeriod?.updated_by_user}
                      />
                    </Box>
                  </Grid>
                </Grid>
              </DialogContent>
            )}

            <DialogActions
              sx={{
                flexWrap: 'wrap',
                rowGap: 2,
              }}
            >
              <Button onClick={handleClose}>Cancel</Button>
              <Button type="submit">Update</Button>
            </DialogActions>
          </form>
        </FormProvider>
      </Dialog>
      <Backdrop open={updateCoursePeriod.isPending}>
        <CircularProgress />
      </Backdrop>
    </QueryClientProvider>
  );

  return {
    openModifyDriveDialog,
    ModifyDriveDialog,
  };
}
